import DOMPurify from 'dompurify';

const RICH_TEXT_ALLOWED_TAGS = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del',
    'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'span',
];

/** Sanitiza HTML de observações/notas (substitui export inseguro do Quill). */
export const sanitizeRichHtml = (html: string): string => {
    if (!html || typeof html !== 'string') return '';
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
        ALLOWED_ATTR: ['class'],
    });
};

export const escapeHtml = (text: string): string => {
    if (!text || typeof text !== 'string') return '';

    const htmlEntities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '`': '&#x60;'
    };

    return text.replace(/[&<>"'`]/g, char => htmlEntities[char] || char);
};

/**
 * Sanitiza texto de mensagem removendo scripts e tags perigosas
 * Mantém o texto legível mas remove código malicioso
 */
export const sanitizeMessageText = (text: string): string => {
    if (!text || typeof text !== 'string') return '';

    // Remove tags script e event handlers
    let sanitized = text
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '')
        .replace(/data:/gi, '');

    // Escapa HTML restante
    sanitized = escapeHtml(sanitized);

    return sanitized;
};

/**
 * Valida e sanitiza URLs para prevenir javascript: e data: URIs
 */
export const sanitizeUrl = (url: string): string => {
    if (!url || typeof url !== 'string') return '';

    const trimmedUrl = url.trim().toLowerCase();

    // Bloqueia protocolos perigosos
    if (
        trimmedUrl.startsWith('javascript:') ||
        trimmedUrl.startsWith('data:') ||
        trimmedUrl.startsWith('vbscript:')
    ) {
        return '';
    }

    return url;
};

/**
 * Src de preview de anúncio CTWA: permite data:image/*;base64 (persistido do jpegThumbnail)
 * e http(s) via sanitizeUrl. Evidência: 1416/1430 msgs com image_url data: URI.
 */
export const sanitizeAdImageSrc = (url: string): string => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (/^data:image\/(jpeg|jpg|png|webp|gif);base64,/i.test(trimmed)) return trimmed;
    return sanitizeUrl(trimmed);
};

/**
 * Regex para detectar URLs em texto
 * Detecta: https://..., http://..., www....
 */
const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

/**
 * Transforma URLs em texto para links clicáveis (HTML)
 * Retorna string com <a> tags para usar com dangerouslySetInnerHTML
 */
export const linkifyText = (text: string): string => {
    if (!text || typeof text !== 'string') return '';

    // Primeiro sanitiza o texto
    const sanitized = sanitizeMessageText(text);

    // Depois converte URLs em links
    return sanitized.replace(URL_REGEX, (url) => {
        // Adiciona https:// se começa com www.
        let href = url;
        if (url.toLowerCase().startsWith('www.')) {
            href = 'https://' + url;
        }

        const safeUrl = sanitizeUrl(href);
        if (!safeUrl) return url;
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-400 underline hover:text-blue-300 break-all">${url}</a>`;
    });
};

/**
 * Verifica se um texto contém URLs
 */
export const hasUrls = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    return URL_REGEX.test(text);
};

/**
 * Formata texto da mensagem aplicando:
 * 1. Sanitização HTML
 * 2. Formatação Markdown (Negrito, Itálico, Riscado, Monospace)
 * 3. Linkificação de URLs
 */
export const formatMessageText = (text: string, isMe: boolean = false): string => {
    if (!text || typeof text !== 'string') return '';

    // Protege URLs/código antes dos marcadores WhatsApp: underscores de UTM são texto literal.
    let tokenPrefix = '\uE000';
    while (text.includes(tokenPrefix)) tokenPrefix += '\uE000';
    const tokens: string[] = [];
    const linkClass = isMe
        ? 'text-white underline hover:text-white break-all'
        : 'text-primary underline hover:text-primary-dark break-all';
    const protectedText = text.replace(/```[\s\S]*?```|https?:\/\/[^\s<>"']+|www\.[^\s<>"']+/gi, (raw, offset: number) => {
        let html: string;
        let suffix = '';
        if (raw.startsWith('```')) {
            html = `<code class="bg-primary/10 px-1 py-0.5 rounded font-mono text-sm">${escapeHtml(raw.slice(3, -3))}</code>`;
        } else {
            const marker = text[offset - 1];
            if (marker && '*_~'.includes(marker) && raw.endsWith(marker)) {
                suffix = marker;
                raw = raw.slice(0, -1);
            }
            const href = sanitizeUrl(/^www\./i.test(raw) ? `https://${raw}` : raw);
            html = `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" class="${linkClass}">${escapeHtml(raw)}</a>`;
        }
        tokens.push(html);
        return `${tokenPrefix}${tokens.length - 1};${suffix}`;
    });
    let formatted = escapeHtml(protectedText);

    // 2. Markdown (Regex simplificada estilo WhatsApp)
    // Negrito: *text*
    formatted = formatted.replace(/\*([^\s*][^*]*[^\s*]|[^\s*])\*/g, '<b>$1</b>');

    // Itálico: _text_
    formatted = formatted.replace(/_([^\s_][^_]*[^\s_]|[^\s_])_/g, '<i>$1</i>');

    // Riscado: ~text~
    formatted = formatted.replace(/~([^\s~][^~]*[^\s~]|[^\s~])~/g, '<del>$1</del>');

    return formatted.replace(new RegExp(`${tokenPrefix}(\\d+);`, 'g'),
        (_match, index: string) => tokens[Number(index)]);
};
